<main class="readiness-page">
  <div class="content-wrapper-parent">
    <div class="content-wrapper sidebar-editor">
  <header class="editor-header">
    <button mat-icon-button class="back-button" type="button" aria-label="Back to dashboard" (click)="goBack()">
      <mat-icon svgIcon="arrow-back"></mat-icon>
    </button>
    <div class="editor-header-content">
      <h2>Understand how ready your data is for AI</h2>
      <p class="editor-subtitle">Configure the data and use case you want to assess, then review the evidence behind each score.</p>
    </div>
    <div class="view-mode-toggle" *ngIf="!isRunning && !showResults">
      <button class="view-mode-toggle-btn" [class.active]="layoutService.getViewMode() === 'simple'" type="button" (click)="setViewMode('simple')">Simple</button>
      <button class="view-mode-toggle-btn" [class.active]="layoutService.getViewMode() === 'advanced'" type="button" (click)="setViewMode('advanced')">Advanced</button>
    </div>
  </header>

  <div class="readiness-content">
    <section class="configuration-panel" *ngIf="!isRunning && !showResults">
      <button class="configuration-heading" type="button" (click)="toggleConfiguration()" [attr.aria-expanded]="configurationExpanded">
        <div class="section-heading">
        <div>
          <p class="eyebrow">ASSESSMENT SETUP</p>
          <h2>Assessment configuration</h2>
          <p class="subtitle">Select the fields that should be included in the check.</p>
        </div>
        </div>
        <mat-icon [svgIcon]="configurationExpanded ? 'expand_less' : 'expand_more'"></mat-icon>
      </button>

      <div class="configuration-body" *ngIf="configurationExpanded">
      <div class="overall-options">
        <mat-form-field appearance="outline">
          <mat-label>Project type</mat-label>
          <mat-select [(ngModel)]="projectType">
            <mat-option value="classification">Classification</mat-option>
            <mat-option value="regression">Regression</mat-option>
            <mat-option value="forecasting">Forecasting</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Use case</mat-label>
          <mat-select [(ngModel)]="useCase">
            <mat-option value="predictive-maintenance">Predictive maintenance</mat-option>
            <mat-option value="customer-insights">Customer insights</mat-option>
            <mat-option value="operational-forecasting">Operational forecasting</mat-option>
          </mat-select>
        </mat-form-field>
      </div>
      </div>

      <div class="section-heading source-heading">
        <div>
          <h2>Selected data sources</h2>
          <p class="subtitle">Each source needs a time field and the columns to assess.</p>
        </div>
        <div class="add-source-control">
          <span>Add data source</span>
          <app-data-source-picker
            [dataSources]="availableDataSources"
            [selectedDataSourceIds]="selectedDataSourceIds"
            [allowMultipleSelection]="true"
            [allowUploadFile]="false"
            [allowDataSourceSelection]="true"
            [allowCreateNew]="false"
            [isLoadingDataSources]="isLoadingDataSources"
            (selectExisting)="openDataSourcePicker()"
            (dataSourceSelected)="addDataSource($event)">
          </app-data-source-picker>
        </div>
      </div>

      <div class="source-list">
        <article class="source-panel" *ngFor="let source of dataSources">
          <div class="source-panel-header">
            <div class="source-name"><mat-icon svgIcon="database"></mat-icon><strong>{{ source.dataSourceName }}</strong></div>
            <button mat-icon-button type="button" aria-label="Remove data source" (click)="removeDataSource(source)">
              <mat-icon svgIcon="close"></mat-icon>
            </button>
          </div>
          <div class="source-options">
            <mat-form-field appearance="outline">
              <mat-label>{{ source.timestampColumns.length ? 'Timestamp column' : 'No Date/DateTime columns available' }}</mat-label>
              <mat-select [(ngModel)]="source.timestampColumn" [disabled]="source.isLoadingSchema || !source.timestampColumns.length">
                <mat-option *ngFor="let column of source.timestampColumns" [value]="column">{{ column }}</mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline" class="column-field">
              <mat-label>Columns to assess</mat-label>
              <mat-select [(ngModel)]="source.selectedColumns" multiple [disabled]="source.isLoadingSchema" (selectionChange)="onColumnsSelectionChange(source, $event.value)">
                <mat-option value="__select_all_columns__">Select all columns</mat-option>
                <mat-option *ngFor="let column of source.columns" [value]="column">{{ column }}</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
        </article>
      </div>

      <p class="empty-state" *ngIf="!dataSources.length">Add at least one data source to continue.</p>
    </section>

    <div class="loading-container" *ngIf="isRunning">
      <mat-spinner></mat-spinner>
      <p class="loading-text">Assessing your data for AI readiness...</p>
    </div>

    <section class="results-panel" *ngIf="showResults">
      <div class="results-heading"><div><p class="eyebrow">SAMPLE RESULTS</p><h2>Readiness overview</h2></div><span class="score">78<span>/100</span></span></div>
      <p class="results-summary">Your data has a strong foundation for AI use. The main opportunity is improving completeness in a few high-value fields.</p>
      <div class="metric-grid">
        <div class="metric"><span>Data quality</span><strong>84</strong><small>Strong</small></div>
        <div class="metric"><span>Feature coverage</span><strong>76</strong><small>Good</small></div>
        <div class="metric"><span>AI suitability</span><strong>74</strong><small>Good</small></div>
      </div>
      <div class="evidence"><mat-icon svgIcon="check-circle"></mat-icon><div><strong>What is working</strong><p>Most selected fields have consistent types and enough historical observations.</p></div></div>
      <div class="evidence warning"><mat-icon svgIcon="warning"></mat-icon><div><strong>Recommended next step</strong><p>Review missing values in the selected feature columns before training a model.</p></div></div>
    </section>
  </div>
  <footer class="readiness-action-bar" *ngIf="!isRunning && !showResults">
    <div class="action-summary">
      <mat-icon svgIcon="tune"></mat-icon>
      <div class="summary-heading"><strong>Ready to assess</strong><span>{{ dataSources.length }} data source{{ dataSources.length === 1 ? '' : 's' }}</span></div>
      <div class="summary-options"><span>{{ projectType | titlecase }}</span><span>{{ useCase | titlecase }}</span></div>
    </div>
    <button mat-flat-button color="primary" class="start-button" type="button" [disabled]="!dataSources.length" (click)="startAssessment()">
      <mat-icon svgIcon="play-arrow"></mat-icon>
      Start AI readiness check
    </button>
  </footer>
    </div>
  </div>
</main>